<!--
  Periodic KPI + agent-activity summary, emailed/WhatsApp'd on a schedule the
  org/product/campaign owner sets, rather than requiring anyone to come look
  at the dashboard themselves. Shared across all three scopes it can be
  configured at (CreateCampaignDialog.vue, ContextFormDialog.vue, and the org
  profile edit view) -- one v-model object carries all six backend fields so
  each call site only needs a couple of lines to wire it into its own form.

  Off by default everywhere -- this is opt-in per scope, not a platform-wide
  behavior change.
-->
<template>
  <div class="dd-settings">
    <!-- Email -->
    <article class="dd-settings__panel" :class="{ 'dd-settings__panel--active': emailEnabled }">
      <button
        type="button"
        class="dd-settings__channel"
        :disabled="disabled"
        :aria-pressed="emailEnabled"
        @click="emailEnabled = !emailEnabled"
      >
        <span class="dd-settings__icon">
          <v-icon size="20">mdi-email-outline</v-icon>
        </span>
        <span class="dd-settings__meta">
          <span class="dd-settings__title">Email</span>
          <span class="dd-settings__subtitle">Send this {{ scopeLabel }}'s KPIs + agent activity by email</span>
        </span>
        <span class="dd-settings__status">{{ emailEnabled ? "On" : "Off" }}</span>
      </button>

      <v-expand-transition>
        <div v-if="emailEnabled" class="dd-settings__details">
          <p class="dd-settings__label">Recipients</p>
          <RecipientChipInput
            :model-value="emailAddresses"
            kind="email"
            :disabled="disabled"
            empty-text="No recipients yet -- add whoever should get this by email below."
            @update:model-value="(v) => patch({ dashboard_digest_email_addresses: v })"
          />
          <p v-if="emailEnabled && !emailAddresses.length" class="dd-settings__warning">
            Email is on but there's nobody to send it to -- add a recipient above, or turn Email off above if you
            don't want this saved.
          </p>

          <v-switch
            v-model="ccAgents"
            label="Also CC this scope's own agents"
            color="primary"
            density="compact"
            hide-details
            class="dd-settings__cc-switch"
            :disabled="disabled"
          />
        </div>
      </v-expand-transition>
    </article>

    <!-- WhatsApp -->
    <article class="dd-settings__panel" :class="{ 'dd-settings__panel--active': whatsappEnabled }">
      <button
        type="button"
        class="dd-settings__channel"
        :disabled="disabled"
        :aria-pressed="whatsappEnabled"
        @click="whatsappEnabled = !whatsappEnabled"
      >
        <span class="dd-settings__icon">
          <v-icon size="20">mdi-whatsapp</v-icon>
        </span>
        <span class="dd-settings__meta">
          <span class="dd-settings__title">WhatsApp</span>
          <span class="dd-settings__subtitle">Send the same summary over WhatsApp too</span>
        </span>
        <span class="dd-settings__status">{{ whatsappEnabled ? "On" : "Off" }}</span>
      </button>

      <v-expand-transition>
        <div v-if="whatsappEnabled" class="dd-settings__details">
          <p class="dd-settings__label">Numbers</p>
          <RecipientChipInput
            :model-value="whatsappNumbers"
            kind="whatsapp"
            :disabled="disabled"
            empty-text="No numbers yet -- add whoever should get this over WhatsApp below."
            @update:model-value="(v) => patch({ dashboard_digest_whatsapp_numbers: v })"
          />
          <p v-if="whatsappEnabled && !whatsappNumbers.length" class="dd-settings__warning">
            WhatsApp is on but there's nobody to send it to -- add a number above, or turn WhatsApp off above if you
            don't want this saved.
          </p>
        </div>
      </v-expand-transition>
    </article>

    <!-- Schedule -->
    <div v-if="emailEnabled || whatsappEnabled" class="dd-settings__schedule">
      <p class="dd-settings__label">Send times</p>
      <!--
        Deliberately NOT a required :rules validator -- this v-select
        registers with whatever ancestor v-form it's rendered inside
        (Vuetify picks up form inputs across component boundaries), and a
        hard rule here blocked saving the ENTIRE host dialog (org/product
        details, unrelated fields and all) the moment a channel was turned
        on with no time picked yet -- someone adding a recipient before
        getting to the time picker had their whole save silently rejected
        with no obvious cause. The warning below nudges without blocking.
      -->
      <v-select
        v-model="digestTimes"
        :items="hourOptions"
        placeholder="Pick one or more times"
        variant="outlined"
        density="compact"
        hide-details="auto"
        multiple
        chips
        closable-chips
        :disabled="disabled"
      >
        <template #prepend-item>
          <v-list-item
            :title="allTimesSelected ? 'Clear all' : 'Select all (every hour)'"
            :disabled="disabled"
            @click="toggleAllTimes"
          >
            <template #prepend>
              <v-checkbox-btn
                :model-value="allTimesSelected"
                :indeterminate="digestTimes.length > 0 && !allTimesSelected"
                :disabled="disabled"
              />
            </template>
          </v-list-item>
          <v-divider class="mt-2" />
        </template>
      </v-select>
      <p v-if="digestTimes.length === 0" class="dd-settings__warning">
        Pick at least one send time, or this won't actually go out -- adding recipients alone doesn't
        schedule anything.
      </p>
      <p class="dd-settings__hint">
        Can fire more than once a day -- each send shows this {{ scopeLabel }}'s activity cumulative since
        midnight, so a later send that day just shows more progress, not a separate window.
      </p>

      <!-- Weekly / Monthly -- independent cadences, each its own on/off,
           all three (daily times above, weekly, monthly) can run at once. -->
      <div class="dd-settings__cadence">
        <div class="dd-settings__cadence-head">
          <span class="dd-settings__label" style="margin: 0">Weekly summary</span>
          <v-switch
            v-model="weeklyEnabled"
            color="primary"
            density="compact"
            hide-details
            :disabled="disabled"
          />
        </div>
        <div v-if="weeklyEnabled" class="dd-settings__cadence-fields">
          <v-select
            v-model="weeklyDay"
            :items="weekdayOptions"
            item-title="label"
            item-value="value"
            placeholder="Day of week"
            variant="outlined"
            density="compact"
            hide-details
            :disabled="disabled"
          />
          <v-select
            v-model="weeklyTime"
            :items="hourOptions"
            placeholder="Time"
            variant="outlined"
            density="compact"
            hide-details
            :disabled="disabled"
          />
        </div>
        <p v-if="weeklyEnabled && (!weeklyDay || !weeklyTime)" class="dd-settings__warning">
          Pick a day and a time, or this won't actually go out.
        </p>
      </div>

      <div class="dd-settings__cadence">
        <div class="dd-settings__cadence-head">
          <span class="dd-settings__label" style="margin: 0">Monthly summary</span>
          <v-switch
            v-model="monthlyEnabled"
            color="primary"
            density="compact"
            hide-details
            :disabled="disabled"
          />
        </div>
        <div v-if="monthlyEnabled" class="dd-settings__cadence-fields">
          <v-select
            v-model="monthlyDay"
            :items="monthDayOptions"
            placeholder="Day of month"
            variant="outlined"
            density="compact"
            hide-details
            :disabled="disabled"
          />
          <v-select
            v-model="monthlyTime"
            :items="hourOptions"
            placeholder="Time"
            variant="outlined"
            density="compact"
            hide-details
            :disabled="disabled"
          />
        </div>
        <p v-if="monthlyEnabled && (!monthlyDay || !monthlyTime)" class="dd-settings__warning">
          Pick a day and a time, or this won't actually go out.
        </p>
        <p v-if="monthlyEnabled && monthlyDay > 28" class="dd-settings__hint">
          Months shorter than day {{ monthlyDay }} send on their own last day instead.
        </p>
      </div>

      <v-switch
        v-model="sendNowValue"
        label="Also send one right now (don't wait for the next scheduled time)"
        color="primary"
        density="compact"
        hide-details
        class="dd-settings__send-now"
        :disabled="disabled"
      />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import RecipientChipInput from '@/components/Shared/RecipientChipInput.vue'

defineOptions({ name: 'DashboardDigestSettings' })

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  disabled: { type: Boolean, default: false },
  // Used only in hint/subtitle copy -- "campaign" / "product" / "organization".
  scopeLabel: { type: String, default: 'campaign' },
  // Separate from modelValue -- this isn't a persisted field, it's a
  // one-shot "also fire an immediate send" flag the host reads at its own
  // save time (see triggerDashboardDigestNow) and is responsible for
  // resetting back to false afterward.
  sendNow: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'update:sendNow'])

const sendNowValue = computed({
  get: () => props.sendNow,
  set: (v) => emit('update:sendNow', !!v),
})

const FIELD_DEFAULTS = {
  send_dashboard_digest_by_email: false,
  dashboard_digest_email_addresses: [],
  send_dashboard_digest_by_whatsapp: false,
  dashboard_digest_whatsapp_numbers: [],
  dashboard_digest_cc_agents: false,
  dashboard_digest_times: [],
  dashboard_digest_weekly_enabled: false,
  dashboard_digest_weekly_day: '',
  dashboard_digest_weekly_time: '',
  dashboard_digest_monthly_enabled: false,
  dashboard_digest_monthly_day: null,
  dashboard_digest_monthly_time: '',
}

function patch(partial) {
  emit('update:modelValue', {
    ...FIELD_DEFAULTS,
    ...props.modelValue,
    ...partial,
  })
}

const emailEnabled = computed({
  get: () => props.modelValue?.send_dashboard_digest_by_email === true,
  set: (v) => patch({ send_dashboard_digest_by_email: !!v, ...(v ? {} : { dashboard_digest_email_addresses: [] }) }),
})

const whatsappEnabled = computed({
  get: () => props.modelValue?.send_dashboard_digest_by_whatsapp === true,
  set: (v) => patch({ send_dashboard_digest_by_whatsapp: !!v, ...(v ? {} : { dashboard_digest_whatsapp_numbers: [] }) }),
})

const ccAgents = computed({
  get: () => props.modelValue?.dashboard_digest_cc_agents === true,
  set: (v) => patch({ dashboard_digest_cc_agents: !!v }),
})

const emailAddresses = computed(() => props.modelValue?.dashboard_digest_email_addresses || [])
const whatsappNumbers = computed(() => props.modelValue?.dashboard_digest_whatsapp_numbers || [])

const digestTimes = computed({
  get: () => props.modelValue?.dashboard_digest_times || [],
  set: (v) => patch({ dashboard_digest_times: v }),
})

const hourOptions = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`)

const allTimesSelected = computed(() => digestTimes.value.length === hourOptions.length)

function toggleAllTimes() {
  digestTimes.value = allTimesSelected.value ? [] : [...hourOptions]
}

// Weekly -- independent cadence, own day + time, alongside the daily times
// above. Turning it off clears day/time together, same "off means empty"
// pattern the Email/WhatsApp toggles already use.
const weekdayOptions = [
  { label: 'Monday', value: 'MON' },
  { label: 'Tuesday', value: 'TUE' },
  { label: 'Wednesday', value: 'WED' },
  { label: 'Thursday', value: 'THU' },
  { label: 'Friday', value: 'FRI' },
  { label: 'Saturday', value: 'SAT' },
  { label: 'Sunday', value: 'SUN' },
]

const weeklyEnabled = computed({
  get: () => props.modelValue?.dashboard_digest_weekly_enabled === true,
  set: (v) => patch({
    dashboard_digest_weekly_enabled: !!v,
    ...(v ? {} : { dashboard_digest_weekly_day: '', dashboard_digest_weekly_time: '' }),
  }),
})
const weeklyDay = computed({
  get: () => props.modelValue?.dashboard_digest_weekly_day || '',
  set: (v) => patch({ dashboard_digest_weekly_day: v }),
})
const weeklyTime = computed({
  get: () => props.modelValue?.dashboard_digest_weekly_time || '',
  set: (v) => patch({ dashboard_digest_weekly_time: v }),
})

// Monthly -- same shape as weekly, day-of-month (1-31) instead of weekday.
// A configured day past a shorter month's end clamps to that month's own
// last day server-side (yandle_event_scheduler) -- the hint below just
// tells the user that's what "31" etc. actually means in short months.
const monthDayOptions = Array.from({ length: 31 }, (_, i) => i + 1)

const monthlyEnabled = computed({
  get: () => props.modelValue?.dashboard_digest_monthly_enabled === true,
  set: (v) => patch({
    dashboard_digest_monthly_enabled: !!v,
    ...(v ? {} : { dashboard_digest_monthly_day: null, dashboard_digest_monthly_time: '' }),
  }),
})
const monthlyDay = computed({
  get: () => props.modelValue?.dashboard_digest_monthly_day ?? null,
  set: (v) => patch({ dashboard_digest_monthly_day: v }),
})
const monthlyTime = computed({
  get: () => props.modelValue?.dashboard_digest_monthly_time || '',
  set: (v) => patch({ dashboard_digest_monthly_time: v }),
})

</script>

<style scoped>
.dd-settings {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.dd-settings__panel {
  border: 1px solid rgba(148, 163, 184, 0.28);
  border-radius: 0.85rem;
  overflow: hidden;
  transition: border-color 0.15s ease;
}

.dd-settings__panel--active {
  border-color: rgba(5, 68, 226, 0.35);
}

.dd-settings__channel {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  background: none;
  border: 0;
  cursor: pointer;
  text-align: left;
}

.dd-settings__icon {
  display: grid;
  place-items: center;
  width: 2.1rem;
  height: 2.1rem;
  border-radius: 0.65rem;
  background: rgba(5, 68, 226, 0.08);
  color: var(--v-theme-primary, #0544e2);
  flex-shrink: 0;
}

.dd-settings__meta {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  flex: 1;
  min-width: 0;
}

.dd-settings__title {
  font-weight: 650;
  font-size: 0.9rem;
}

.dd-settings__subtitle {
  font-size: 0.76rem;
  opacity: 0.65;
}

.dd-settings__status {
  font-size: 0.72rem;
  font-weight: 650;
  opacity: 0.55;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.dd-settings__details {
  padding: 0 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.dd-settings__label {
  font-size: 0.72rem;
  font-weight: 650;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.6;
  margin: 0;
}

.dd-settings__warning {
  font-size: 0.76rem;
  color: #b45309;
  margin: 0.35rem 0 0;
}

.dd-settings__cc-switch {
  margin-top: 0.25rem;
}

.dd-settings__schedule {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.1rem 0.1rem 0;
}

.dd-settings__hint {
  font-size: 0.76rem;
  opacity: 0.6;
  margin: 0;
}

.dd-settings__send-now {
  margin-top: 0.6rem;
}

.dd-settings__cadence {
  border-top: 1px solid rgba(148, 163, 184, 0.2);
  padding-top: 0.65rem;
  margin-top: 0.15rem;
}

.dd-settings__cadence-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.dd-settings__cadence-fields {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.dd-settings__cadence-fields > * {
  flex: 1;
  min-width: 0;
}
</style>
